import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { formatDistanceToNow, parseISO } from "date-fns";
import {
  History, Trophy, ShieldCheck, ShieldOff, ArrowRightCircle, MailPlus,
  MessageSquare, Plus, Trash2, RefreshCw,
} from "lucide-react";

import { api } from "@/lib/api";

const DECISION_ACTIONS = new Set([
  "winner_selected",
  "winner_approved",
  "winner_approval_revoked",
  "quote_added",
  "quote_submitted",   // portal-submitted quote (activity.record_public)
  "quote_removed",
  "converted",
  "portal_broadcast",
  "status_changed",
  "message_sent",
]);

const FILTERS = [
  { id: "all", label: "All", match: () => true },
  { id: "approvals", label: "Approvals", match: (e) =>
      e.action === "winner_approved" || e.action === "winner_approval_revoked" || e.action === "winner_selected" },
  { id: "quotes", label: "Quotes", match: (e) =>
      e.action === "quote_added" || e.action === "quote_submitted"
      || e.action === "quote_removed" || e.action === "converted" },
  { id: "messages", label: "Messages", match: (e) =>
      e.action === "message_sent" || e.action === "portal_broadcast" },
];

const CFG = {
  winner_selected: {
    icon: Trophy,
    label: "picked a winning quote",
    tone: "bg-amber-100 text-amber-800 ring-amber-200",
  },
  winner_approved: {
    icon: ShieldCheck,
    label: "approved the winner",
    tone: "bg-emerald-100 text-emerald-700 ring-emerald-200",
  },
  winner_approval_revoked: {
    icon: ShieldOff,
    label: "revoked the approval",
    tone: "bg-red-100 text-red-700 ring-red-200",
  },
  quote_added: {
    icon: Plus,
    label: "recorded a supplier quote",
    tone: "bg-sky-100 text-sky-700 ring-sky-200",
  },
  quote_submitted: {
    icon: Plus,
    label: "submitted a quote via portal",
    tone: "bg-sky-100 text-sky-700 ring-sky-200",
  },
  quote_removed: {
    icon: Trash2,
    label: "removed a quote",
    tone: "bg-slate-100 text-slate-700 ring-slate-200",
  },
  converted: {
    icon: ArrowRightCircle,
    label: "converted to a PO",
    tone: "bg-indigo-100 text-indigo-700 ring-indigo-200",
  },
  portal_broadcast: {
    icon: MailPlus,
    label: "emailed portal link",
    tone: "bg-amber-100 text-amber-800 ring-amber-200",
  },
  status_changed: {
    icon: RefreshCw,
    label: "changed status",
    tone: "bg-slate-100 text-slate-700 ring-slate-200",
  },
  message_sent: {
    icon: MessageSquare,
    label: "posted a message",
    tone: "bg-sky-100 text-sky-700 ring-sky-200",
  },
};

function niceExtra(entry) {
  const meta = entry.meta || {};
  if (entry.action === "winner_approved" && meta.approver)
    return `by ${meta.approver}`;
  if (entry.action === "quote_added" && meta.supplier)
    return `from ${meta.supplier}${meta.subtotal ? ` · ${Number(meta.subtotal).toLocaleString()}` : ""}`;
  if (entry.action === "converted" && meta.to_po) return `→ ${meta.to_po}`;
  if (entry.action === "portal_broadcast")
    return `${meta.sent || 0} sent, ${meta.skipped || 0} skipped`;
  if (entry.action === "status_changed" && meta.to) return `→ ${meta.to}`;
  if (entry.action === "message_sent")
    return `${meta.from || "?"} · "${(meta.preview || "").slice(0, 60)}"`;
  return "";
}

/**
 * Small vertical timeline of decision-affecting audit-log events for an RFQ.
 * Uses the existing audit-log endpoint and filters client-side.
 */
export function RfqDecisionTimeline({ rfqId }) {
  const [filter, setFilter] = useState("all");
  const { data = [], isLoading } = useQuery({
    queryKey: ["audit-logs", "rfq", rfqId, "decision-timeline"],
    queryFn: () =>
      api.listAuditLogs({
        resource_type: "rfq",
        resource_id: rfqId,
        limit: 50,
      }),
    enabled: !!rfqId,
    refetchOnWindowFocus: false,
  });

  const decisions = data.filter((e) => DECISION_ACTIONS.has(e.action));
  const activeFilter = FILTERS.find((f) => f.id === filter) || FILTERS[0];
  const visible = decisions.filter(activeFilter.match);
  const counts = {
    all: decisions.length,
    approvals: decisions.filter(FILTERS[1].match).length,
    quotes: decisions.filter(FILTERS[2].match).length,
    messages: decisions.filter(FILTERS[3].match).length,
  };

  return (
    <div
      className="border border-border rounded-md bg-white p-3"
      data-testid="rfq-decision-timeline"
    >
      <div className="flex items-center gap-2 mb-2">
        <History className="size-3.5 text-primary" />
        <p className="overline">Decision timeline</p>
        <span className="text-[10px] text-muted-foreground ml-auto">
          {visible.length} of {decisions.length}
        </span>
      </div>

      {decisions.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2" data-testid="rfq-decision-filters">
          {FILTERS.map((f) => {
            const active = filter === f.id;
            return (
              <button
                key={f.id}
                data-testid={`rfq-decision-filter-${f.id}`}
                onClick={() => setFilter(f.id)}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ring-1 ring-inset transition ${
                  active
                    ? "bg-slate-800 text-white ring-slate-800"
                    : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50"
                }`}
              >
                {f.label}
                <span className={`text-[9px] mono ${active ? "opacity-80" : "text-slate-500"}`}>
                  {counts[f.id]}
                </span>
              </button>
            );
          })}
        </div>
      )}
      {isLoading ? (
        <p className="text-xs text-muted-foreground">Loading…</p>
      ) : visible.length === 0 ? (
        <p
          className="text-xs text-muted-foreground italic py-2"
          data-testid="rfq-decision-timeline-empty"
        >
          {decisions.length === 0
            ? "Decisions like picking a winner, sign-offs and revokes will appear here."
            : `No ${activeFilter.label.toLowerCase()} yet.`}
        </p>
      ) : (
        <ol className="relative border-l-2 border-slate-100 ml-3 space-y-3 max-h-72 overflow-y-auto pr-1">
          {visible.map((e) => {
            const cfg = CFG[e.action] || {
              icon: History,
              label: e.action,
              tone: "bg-slate-100 text-slate-700 ring-slate-200",
            };
            const Icon = cfg.icon;
            const extra = niceExtra(e);
            return (
              <li
                key={e.id}
                className="pl-3 relative"
                data-testid={`rfq-decision-${e.action}`}
              >
                <span
                  className={`absolute -left-[13px] top-0 size-6 grid place-items-center rounded-full ring-1 ring-inset ring-white shadow-sm ${cfg.tone}`}
                >
                  <Icon className="size-3" />
                </span>
                <p className="text-xs text-foreground leading-tight">
                  <span className="font-semibold">{e.actor_name || "system"}</span>{" "}
                  <span className="text-muted-foreground">{cfg.label}</span>
                  {extra && (
                    <span className="mono text-[11px] px-1.5 py-0.5 rounded bg-slate-50 border border-slate-200 text-slate-700 ml-1">
                      {extra}
                    </span>
                  )}
                </p>
                <p className="text-[10px] text-muted-foreground mt-0.5 mono">
                  {e.actor_role && (
                    <span className="uppercase tracking-wider mr-2">
                      {e.actor_role}
                    </span>
                  )}
                  {timeAgo(e.ts)}
                </p>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

function timeAgo(iso) {
  try {
    return formatDistanceToNow(parseISO(iso), { addSuffix: true });
  } catch {
    return iso;
  }
}

export default RfqDecisionTimeline;
